<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { api, errorText, useSession } from "../api";
import { UploadIcon } from "../icons";
const props = defineProps<{
  dataset: any;
  disabled?: boolean;
  existing?: boolean;
}>();
const uploadId = defineModel<string>({ required: true });
const emit = defineEmits<{ busy: [boolean] }>();
const session = useSession(),
  busy = ref(false),
  error = ref(""),
  filename = ref("");
const previewFailed = ref(false);
const errorElement = ref<HTMLElement>();
watch(error, async (message) => {
  if (!message) return;
  await nextTick();
  errorElement.value?.scrollIntoView({ block: "nearest" });
});
let attempt = 0;
const previewUrl = computed(() =>
  uploadId.value
    ? `/api/datasets/${props.dataset.id}/icon-uploads/${uploadId.value}?organization=${session.organization}`
    : props.existing && !previewFailed.value
      ? `/api/datasets/${props.dataset.id}/favicon?organization=${session.organization}&v=${props.dataset.favicon_revision}`
      : "",
);
async function choose(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  error.value = "";
  if (file.size > 5 * 1024 * 1024) {
    error.value = "Billedet må højst fylde 5 MB. Vælg et mindre billede.";
    return;
  }
  const current = ++attempt;
  busy.value = true;
  emit("busy", true);
  try {
    const body = new FormData();
    body.append("file", file);
    const result = await api(`/api/datasets/${props.dataset.id}/icon-uploads`, {
      method: "POST",
      body,
    });
    if (current !== attempt) return;
    uploadId.value = result.id;
    filename.value = file.name;
    previewFailed.value = false;
  } catch (e) {
    if (current === attempt) error.value = errorText(e);
  } finally {
    if (current === attempt) {
      busy.value = false;
      emit("busy", false);
    }
  }
}
function previewError() {
  uploadId.value = "";
  previewFailed.value = true;
  error.value = "Billedet kunne ikke vises. Vælg filen igen, før du gemmer.";
}
onUnmounted(() => {
  attempt++;
  emit("busy", false);
});
</script>
<template>
  <div class="image-upload" :aria-busy="busy">
    <div class="image-preview-row">
      <div class="image-preview">
        <img
          v-if="previewUrl"
          :src="previewUrl"
          width="64"
          height="64"
          alt="Forhåndsvisning af datasættets ikon"
          @error="previewError"
        />
        <UploadIcon v-else :size="26" />
      </div>
      <div class="image-preview-copy" aria-live="polite">
        <strong>{{
          busy
            ? "Klargør billede…"
            : uploadId
              ? "Klar til at gemme"
              : existing
                ? "Dit uploadede billede"
                : "Brug dit eget billede"
        }}</strong>
        <span v-if="filename" class="upload-filename">{{ filename }}</span>
        <span class="muted small"
          >Billedet tilpasses automatisk uden beskæring.</span
        >
      </div>
    </div>
    <label class="image-file-picker" :class="{ disabled: disabled || busy }">
      <UploadIcon :size="16" />
      {{ previewUrl ? "Vælg et andet billede" : "Vælg billede" }}
      <input
        id="dataset-image-upload"
        type="file"
        accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
        :disabled="disabled || busy"
        aria-describedby="dataset-image-hint dataset-image-save"
        :aria-invalid="!!error"
        @change="choose"
      />
    </label>
    <p id="dataset-image-hint" class="muted small">
      PNG, JPG eller WebP. Højst 5 MB og 25 megapixels.
    </p>
    <p v-if="error" ref="errorElement" class="error small" role="alert">
      {{ error
      }}<span v-if="uploadId"> Det viste billede er stadig valgt.</span>
    </p>
    <p id="dataset-image-save" class="muted small">
      Billedet bruges først, når du gemmer ændringerne.
    </p>
  </div>
</template>
<style
  scoped
  lang="scss"
  src="../styles/components/DatasetImageUpload.scss"
></style>
